import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Form } from 'react-bootstrap';
import { quickbooksApi } from '../services/api';
import { FiDollarSign, FiCheck, FiX, FiRefreshCw, FiTrash2, FiSettings } from 'react-icons/fi';

const QuickBooksPage = () => {
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);
  const [syncSettings, setSyncSettings] = useState({
    syncCustomers: true,
    syncInvoices: true,
    syncPayments: true
  });

  useEffect(() => {
    fetchIntegration();
    
    // Check for success/error from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success')) {
      setSuccess('QuickBooks connected successfully!');
      fetchIntegration();
    }
    if (urlParams.get('error')) {
      setError('Failed to connect QuickBooks. Please try again.');
    }
  }, []);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await quickbooksApi.getIntegration();
      setIntegration(response.data.data.integration);
      if (response.data.data.integration) {
        setSyncSettings({
          syncCustomers: response.data.data.integration.syncCustomers,
          syncInvoices: response.data.data.integration.syncInvoices,
          syncPayments: response.data.data.integration.syncPayments
        });
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load QuickBooks integration');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setProcessing(true);
      const response = await quickbooksApi.initiateAuth();
      if (response.data.data.authUrl) {
        window.location.href = response.data.data.authUrl;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate QuickBooks connection');
      setProcessing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect QuickBooks? This will stop all syncing.')) {
      return;
    }

    try {
      await quickbooksApi.disconnect();
      await fetchIntegration();
      setSuccess('QuickBooks disconnected successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disconnect QuickBooks');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await quickbooksApi.updateSyncSettings(syncSettings);
      setSuccess('Sync settings updated successfully');
      await fetchIntegration();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update sync settings');
    }
  };

  const handleTestConnection = async () => {
    try {
      setProcessing(true);
      setError('');
      const response = await quickbooksApi.testConnection();
      if (response.data.success) {
        setSuccess(`Connection successful! Connected to: ${response.data.data.companyName || 'QuickBooks'}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Connection test failed. Please check your QuickBooks connection.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loading-spinner" style={{ margin: '0 auto', width: '40px', height: '40px' }}></div>
        <p className="mt-3">Loading QuickBooks integration...</p>
      </div>
    );
  }

  const isConnected = integration?.isActive;

  return (
    <div className="quickbooks-page">
      <h2 className="mb-4">QuickBooks Online Integration</h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Connection Status */}
      <Card className="mb-4 border-primary">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Connection Status</h5>
            {isConnected && (
              <Badge bg="success">
                <FiCheck className="me-1" />
                Connected
              </Badge>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {!isConnected ? (
            <div>
              <p className="mb-4">
                Connect your QuickBooks Online account to automatically sync customers, invoices, and payments. 
                When you create appointments with paid services, they will be automatically synced to your QuickBooks account.
              </p>
              <p className="text-muted small mb-4">
                <strong>Note:</strong> Each business account connects to its own QuickBooks company. 
                Your QuickBooks data is private to your business and will not be shared with other users.
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={handleConnect}
                disabled={processing}
              >
                <FiDollarSign className="me-2" />
                Connect Your QuickBooks Account
              </Button>
            </div>
          ) : (
            <div>
              <div className="mb-3">
                <p className="mb-1"><strong>QuickBooks Company ID:</strong> {integration.realmId}</p>
                <p className="mb-1 text-muted small">
                  Connected {new Date(integration.createdAt).toLocaleDateString()}
                </p>
                {integration.tokenExpiresAt && (
                  <p className="mb-0 text-muted small">
                    Token expires: {new Date(integration.tokenExpiresAt).toLocaleString()}
                  </p>
                )}
              </div>
              <Alert variant="info" className="small mb-3">
                This QuickBooks connection is specific to your business account. 
                Only your appointments and data will be synced to this QuickBooks company.
              </Alert>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handleTestConnection}
                  disabled={processing}
                >
                  <FiRefreshCw className="me-1" />
                  Test Connection
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={handleDisconnect}
                >
                  <FiTrash2 className="me-1" />
                  Disconnect Your QuickBooks Account
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Sync Settings */}
      {isConnected && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              <FiSettings className="me-2" />
              Sync Settings
            </h5>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Check
                type="switch"
                id="syncCustomers"
                label="Sync Customers"
                checked={syncSettings.syncCustomers}
                onChange={(e) => setSyncSettings({ ...syncSettings, syncCustomers: e.target.checked })}
                className="mb-3"
              />
              <p className="text-muted small mb-3">
                Automatically create or update customers in QuickBooks when appointments are booked.
              </p>

              <Form.Check
                type="switch"
                id="syncInvoices"
                label="Sync Invoices"
                checked={syncSettings.syncInvoices}
                onChange={(e) => setSyncSettings({ ...syncSettings, syncInvoices: e.target.checked })}
                className="mb-3"
              />
              <p className="text-muted small mb-3">
                Automatically create invoices in QuickBooks for appointments with paid services.
              </p>

              <Form.Check
                type="switch"
                id="syncPayments"
                label="Sync Payments"
                checked={syncSettings.syncPayments}
                onChange={(e) => setSyncSettings({ ...syncSettings, syncPayments: e.target.checked })}
                className="mb-3"
              />
              <p className="text-muted small mb-3">
                Sync payment information to QuickBooks when payments are received.
              </p>

              <Button
                variant="primary"
                onClick={handleUpdateSettings}
              >
                <FiRefreshCw className="me-1" />
                Save Settings
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Info Card */}
      <Card className="mt-4 bg-light">
        <Card.Body>
          <h6>How It Works</h6>
          <ul className="mb-0">
            <li>When you create an appointment with a paid service, a customer record is created/updated in QuickBooks</li>
            <li>An invoice is automatically generated in QuickBooks for the service</li>
            <li>All customer information (name, email, phone) is synced to QuickBooks</li>
            <li>You can enable or disable specific sync features above</li>
            <li>Data syncs automatically - no manual intervention needed</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
};

export default QuickBooksPage;
